import { useRef, useEffect } from 'react';
import { XMarkIcon, CubeIcon } from '@heroicons/react/24/outline';
import {
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CLOSE_X,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { ProductForm } from '@features/products/components/ProductForm';
import { useCreateProduct } from '@features/products/hooks/useProducts';
import type { Product } from '@features/products/types/product.types';
import { createPortal } from 'react-dom';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductCreated?: (product: Product) => void;
}

export function ProductFormModal({ isOpen, onClose, onProductCreated }: ProductFormModalProps) {
  const createProductMutation = useCreateProduct();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const timer = setTimeout(() => {
        const firstInput = panelRef.current?.querySelector('input, select, textarea') as HTMLElement;
        if (firstInput) {
          firstInput.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = async (data: Partial<Product>) => {
    try {
      const newProduct = await createProductMutation.mutateAsync(data);
      if (onProductCreated) {
        onProductCreated(newProduct);
      }
      onClose();
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on backdrop, not on children
    if (e.target === e.currentTarget) {
      // Don't close - we want explicit close button click
    }
  };

  if (!isOpen) return null;

  // Simple modal without Headless UI Dialog - avoids focus trap conflicts
  const modalContent = (
    <div
      className="fixed inset-0"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
      data-testid="invoice-wizard-product-form-modal"
    >
      {/* Backdrop */}
      <div
        className={cn(MODAL_BACKDROP, 'transition-opacity')}
        style={{ zIndex: 99999 }}
      />

      {/* Modal container */}
      <div
        className="fixed inset-0 overflow-y-auto"
        style={{ zIndex: 100000 }}
      >
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Modal panel */}
          <div
            ref={panelRef}
            className={cn(
              'flex max-h-[calc(100vh-2rem)] max-w-2xl flex-col animate-in fade-in zoom-in-95 duration-200',
              MODAL_PANEL
            )}
            style={{ zIndex: 100001 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ---------- HEAD ---------- */}
            <div className={cn(MODAL_HEAD, 'shrink-0')}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3.5">
                  <span className={MODAL_ICON_TILE}>
                    <CubeIcon />
                  </span>
                  <div className="min-w-0">
                    <div className={MODAL_KICKER}>Catálogo · Producto</div>
                    <h3 className={cn(MODAL_TITLE, 'mt-1')}>Crear Nuevo Producto</h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  aria-label="Cerrar"
                  data-testid="invoice-wizard-product-form-modal-close-button"
                  className={MODAL_CLOSE_X}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* ---------- BODY ---------- */}
            <div className={cn(MODAL_BODY, 'min-h-0 flex-1')}>
              <ProductForm
                onSubmit={handleSubmit}
                onCancel={handleClose}
                isLoading={createProductMutation.isPending}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render via portal to escape parent stacking context completely
  return createPortal(modalContent, document.body);
}
